/* =========================================================================
   rbacService — the ONLY seam between the RBAC UI and its data source.

   Now backed by the real backend (Express + Prisma) at /api/v1/rbac/*.
   The function signatures and resolved shapes are the contract the UI depends
   on — they are identical to the former mock layer, so no page component had to
   change when we cut over from localStorage to the API.

   Backend response envelope: { status, message, data }. We unwrap `.data.data`.
   Errors are surfaced as Error(message) so existing try/catch in the pages keep
   showing the same toasts.

   Endpoint map:
     listUsers     → GET    /rbac/users?search=&role=&status=&page=&pageSize=
     getUser       → GET    /rbac/users/:id
     createUser    → POST   /rbac/users
     updateUser    → PUT    /rbac/users/:id
     deleteUser    → DELETE /rbac/users/:id
     setUserStatus → PATCH  /rbac/users/:id/status
     resetPassword → POST   /rbac/users/:id/reset-password
     listRoles     → GET    /rbac/roles
     getRole       → GET    /rbac/roles/:id
     createRole    → POST   /rbac/roles
     updateRole    → PUT    /rbac/roles/:id
     deleteRole    → DELETE /rbac/roles/:id
   ========================================================================= */

import { get, post, put, del, patch } from "../api/methods";
import { normalizePermissions } from "./permissions";

// Pull the meaningful message out of an axios error, falling back sensibly.
const errMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.errors ||
  error?.message ||
  fallback;

// Unwrap the backend envelope { status, message, data } → data.
const unwrap = (res) => res?.data?.data;

// =========================================================================
// USERS
// =========================================================================

export const listUsers = async ({
  search = "",
  role = "",
  status = "",
  page = 1,
  pageSize = 8,
} = {}) => {
  try {
    const res = await get("/rbac/users", {
      params: { search, role, status, page, pageSize },
    });
    // Backend returns { data: [...], total, page, pageSize }
    return unwrap(res);
  } catch (error) {
    throw new Error(errMessage(error, "Failed to load users"));
  }
};

export const getUser = async (id) => {
  try {
    const res = await get(`/rbac/users/${id}`);
    return unwrap(res);
  } catch (error) {
    throw new Error(errMessage(error, "User not found"));
  }
};

export const createUser = async (payload) => {
  try {
    const res = await post("/rbac/users", payload);
    return unwrap(res);
  } catch (error) {
    // 409 = the email already belongs to someone. That is an offer, not a dead
    // end: the caller can re-send with grantExisting to give that person the
    // CMS role (emails are unique, so there is no second account to create).
    // Carry the details on the Error so the form can ask, instead of
    // flattening it to a message like every other failure.
    if (error?.response?.status === 409) {
      const err = new Error(errMessage(error, "This email already has an account"));
      err.existingAccount = error?.response?.data?.data?.existingAccount || {};
      err.isExistingAccount = true;
      throw err;
    }
    throw new Error(errMessage(error, "Failed to create user"));
  }
};

export const updateUser = async (id, payload) => {
  try {
    const res = await put(`/rbac/users/${id}`, payload);
    return unwrap(res);
  } catch (error) {
    throw new Error(errMessage(error, "Failed to update user"));
  }
};

export const deleteUser = async (id) => {
  try {
    const res = await del(`/rbac/users/${id}`);
    return unwrap(res);
  } catch (error) {
    throw new Error(errMessage(error, "Failed to delete user"));
  }
};

export const setUserStatus = async (id, status) => {
  try {
    const res = await patch(`/rbac/users/${id}/status`, { status });
    return unwrap(res);
  } catch (error) {
    throw new Error(errMessage(error, "Failed to update status"));
  }
};

/** Email the user a link to set a new password (admin never types it). */
export const resetPassword = async (id) => {
  try {
    const res = await post(`/rbac/users/${id}/reset-password`);
    return unwrap(res);
  } catch (error) {
    throw new Error(errMessage(error, "Failed to send reset link"));
  }
};

// =========================================================================
// ROLES
// =========================================================================

export const listRoles = async () => {
  try {
    const res = await get("/rbac/roles");
    const roles = unwrap(res) || [];
    // Defensive normalize so the matrix always has every current cell.
    return roles.map((r) => ({
      ...r,
      permissions: normalizePermissions(r.permissions),
    }));
  } catch (error) {
    throw new Error(errMessage(error, "Failed to load roles"));
  }
};

export const getRole = async (id) => {
  try {
    const res = await get(`/rbac/roles/${id}`);
    const role = unwrap(res);
    return { ...role, permissions: normalizePermissions(role.permissions) };
  } catch (error) {
    throw new Error(errMessage(error, "Role not found"));
  }
};

export const createRole = async (payload) => {
  try {
    const res = await post("/rbac/roles", payload);
    return unwrap(res);
  } catch (error) {
    throw new Error(errMessage(error, "Failed to create role"));
  }
};

export const updateRole = async (id, payload) => {
  try {
    const res = await put(`/rbac/roles/${id}`, payload);
    return unwrap(res);
  } catch (error) {
    throw new Error(errMessage(error, "Failed to update role"));
  }
};

export const deleteRole = async (id) => {
  try {
    const res = await del(`/rbac/roles/${id}`);
    return unwrap(res);
  } catch (error) {
    throw new Error(errMessage(error, "Failed to delete role"));
  }
};

// ---- invites --------------------------------------------------------------

/** Re-send an invite email to a staff user still in the "invited" state. */
export const resendInvite = async (id) => {
  try {
    const res = await post(`/rbac/users/${id}/resend-invite`);
    return unwrap(res);
  } catch (error) {
    throw new Error(errMessage(error, "Failed to resend invitation"));
  }
};

/** PUBLIC: validate an invite token and get the invitee's name/email so the
 *  accept-invite page can greet them. Throws if invalid/expired. */
export const getInviteInfo = async (token) => {
  try {
    const res = await get("/rbac/invite-info", { params: { token } });
    return unwrap(res);
  } catch (error) {
    throw new Error(errMessage(error, "This invitation is invalid or has expired"));
  }
};

/** PUBLIC: accept an invite by setting a password. Activates the account. */
export const acceptInvite = async (token, password) => {
  try {
    const res = await post("/rbac/accept-invite", { token, password });
    return unwrap(res);
  } catch (error) {
    throw new Error(errMessage(error, "Failed to accept invitation"));
  }
};

// ---- current user ---------------------------------------------------------

/** The signed-in user's current effective permissions, fresh from the server.
 *  Called on app load to refresh the cached UI-gating map (so it can't go stale
 *  after an admin edits the caller's role). Returns the { module: { action } }
 *  map, or null if it can't be fetched (caller keeps the cached copy). */
export const fetchMyPermissions = async () => {
  try {
    const res = await get("/rbac/me");
    return unwrap(res)?.permissions ?? null;
  } catch {
    return null;
  }
};
